import {
  ComparatorContext,
  GraphSolarPanelsApi,
  getCalculateSessionId,
  useField,
  useFormService,
} from '@kizi/comparator-core';
import { IconV2Props } from '@kizi/shared-components';
import React, { useContext, useEffect, useMemo } from 'react';
import { AddressInputProps } from '../../Address';
import {
  AddressWithApiCall,
  AddressWithApiCallProps,
  ApiSuccessParam,
} from './AddressWithApiCall';

export interface AddressWithContextProps
  extends Pick<AddressWithApiCallProps, 'apiSuccessCallback'> {
  fieldNames: {
    addition: string;
    houseNumber: string;
    postcode: string;
    city: string;
    street: string;
    fullAddress: string;
    streetViewImage?: string;
    houseNumberWithAddition?: string;
  };
  inputProps?: {
    addition?: Partial<AddressInputProps>;
    houseNumber?: Partial<AddressInputProps>;
    postcode?: Partial<AddressInputProps>;
  };
  autoForward?: boolean;
  iconProps?: IconV2Props;
  pTextVariant?: string;
  getImage?: boolean;
}

const getFieldProps = (field) => ({
  _label: field.label,
  name: field.name,
  validator: field.validator,
  transformers: field.transformers,
  value: field.value,
  _error: field.errors,
});

export const AddressWithContext: React.FC<AddressWithContextProps> = ({
  fieldNames,
  inputProps,
  apiSuccessCallback,
  autoForward = false,
  iconProps,
  pTextVariant,
  getImage = false,
}) => {
  const formService = useFormService();
  const postcodeField = useField(fieldNames.postcode, formService);
  const houseNumberField = useField(fieldNames.houseNumber, formService);
  const additionField = useField(fieldNames.addition, formService);
  const cityField = useField(fieldNames.city, formService);
  const streetField = useField(fieldNames.street, formService);
  const fullAddressField = useField(fieldNames.fullAddress, formService);
  const streetViewImageField = useField(
    fieldNames?.streetViewImage || '',
    formService,
  );
  const houseNumberWithAdditionField = useField(
    fieldNames?.houseNumberWithAddition || '',
    formService,
  );

  const { api } = useContext(ComparatorContext);

  useEffect(() => {
    (async () => {
      if (getImage && postcodeField?.value && houseNumberField?.value) {
        const { preAssignedUrl } = (await (
          api as GraphSolarPanelsApi
        ).getStreetViewImage(
          String(postcodeField?.value),
          Number(houseNumberField?.value),
          additionField?.value ? String(additionField?.value) : null,
          getCalculateSessionId(),
          `${process.env.KIZI_PUBLIC_DOMAIN}/shared/services/graphql`,
        )) as { preAssignedUrl: string };
        if (preAssignedUrl) {
          streetViewImageField?.set({ value: preAssignedUrl });
        }
      }
    })();
  }, [fullAddressField]);

  const postcode = { ...getFieldProps(postcodeField), ...inputProps?.postcode };
  const houseNumber = {
    ...getFieldProps(houseNumberField),
    ...inputProps?.houseNumber,
  };
  const addition = { ...getFieldProps(additionField), ...inputProps?.addition };
  const callback = useMemo<typeof apiSuccessCallback>(() => {
    if (!autoForward) return apiSuccessCallback;
    return (res: ApiSuccessParam) => {
      postcodeField?.set({ value: res['postcode'] });
      houseNumberField?.set({ value: res['houseNumber'] });
      additionField?.set({ value: res['addition'] || '' });
      houseNumberWithAdditionField?.set({
        value: res['addition']
          ? `${res['houseNumber']}-${res['addition']}`
          : res['houseNumber'],
      });
      cityField?.set({ value: res['city'] });
      streetField?.set({ value: res['street'] });
      fullAddressField?.set({ value: res['fullAddress'] });
      if (apiSuccessCallback) apiSuccessCallback(res);
    };
  }, [
    autoForward,
    fullAddressField,
    streetField,
    cityField,
    postcodeField,
    houseNumberField,
    additionField,
    apiSuccessCallback,
  ]);
  const city = cityField ? cityField.value : undefined;
  const street = streetField ? streetField.value : undefined;
  if (
    !postcodeField ||
    !postcodeField.active ||
    !houseNumberField ||
    !houseNumberField.active ||
    !additionField ||
    !cityField ||
    !streetField ||
    !fullAddressField
  )
    return null;

  return (
    <AddressWithApiCall
      postcode={postcode}
      houseNumber={houseNumber}
      addition={addition}
      city={city as string}
      street={street as string}
      apiSuccessCallback={callback}
      iconProps={iconProps}
      pTextVariant={pTextVariant}
    />
  );
};
